/**
 * The chrome's API calls (wp/shell §6). Modules never call these endpoints themselves: they use the session API
 * (`./index.ts`) and the counts store.
 */
import { http, type ApiResponse } from '@/api/http'
import type { ApiData } from '@/api/types'

import type { LoginAnswer, LoginSessionData, Profile, UserType, Workspace } from '../stores/session'

/** `GET settings/timeformats`: PHP key → display pattern (`{"d/m/Y": "DD/MM/YYYY", …}`). */
export function getTimeformats(): Promise<ApiResponse<Record<string, string>>> {
  return http.get<Record<string, string>>('settings/timeformats')
}

/** `POST users/publicLogin` without a body: refreshes a live session (same token), 401 for a dead one. */
export function refreshLogin(): Promise<ApiResponse<LoginAnswer>> {
  return http.post<LoginAnswer>('users/publicLogin', undefined, { skipSessionDefaults: true })
}

/**
 * `POST users/logout`: expires the token on the API (D-43). Never the 401 handler's business: a dead token is already
 * logged out, and the caller ignores any failure (an unreachable API must not keep anyone signed in).
 */
export function postLogout(): Promise<ApiResponse<unknown>> {
  return http.post<unknown>('users/logout', undefined, {
    skipSessionDefaults: true,
    skipUnauthorizedHandler: true,
    timeout: 5000,
  })
}

export type OtherAccount = ApiData<'/users/myAccounts', 'get'>[number]

/** `GET users/myAccounts`: the caller's other ACTIVE accounts with the same login. */
export function getMyAccounts(): Promise<ApiResponse<OtherAccount[]>> {
  return http.get<OtherAccount[]>('users/myAccounts')
}

/** `GET system/workspaces`: the caller's active workspaces. */
export function getWorkspaces(): Promise<ApiResponse<{ workspaces: Workspace[]; current_workspace: number | null }>> {
  return http.get<{ workspaces: Workspace[]; current_workspace: number | null }>('system/workspaces')
}

/** The account: `GET candidates/profile` for a Freelancer, `GET users/profile` otherwise (legacy navbar). */
export function getAccountProfile(userType: UserType): Promise<ApiResponse<Profile>> {
  return http.get<Profile>(userType === 'Freelancer' ? 'candidates/profile' : 'users/profile')
}

export function postSwitchCompany(companyId: number): Promise<ApiResponse<unknown>> {
  return http.post<unknown>('main/switchCompany', { company_id: companyId })
}

export function postSwitchSoftware(softwareId: number): Promise<ApiResponse<unknown>> {
  return http.post<unknown>('main/switchSoftware', { software_id: softwareId })
}

/** `POST users/toggleAccount`: the session of another of the caller's accounts (same login), after the password. */
export function postToggleAccount(userId: number, password: string): Promise<ApiResponse<LoginSessionData>> {
  return http.post<LoginSessionData>('users/toggleAccount', { user_id: userId, password })
}

/** `POST companies`: a new workspace owned by the caller (Client only). */
export function postCompany(label: string): Promise<ApiResponse<Workspace>> {
  return http.post<Workspace>('companies', { Label: label })
}

